import { useCallback, useMemo, useState } from "react";
import { css } from "@/domains/emotion";
import {
  MdsDropdown,
  MdsDropdownItem,
  MdsDropdownItemKind,
  MdsDropdownProps,
  MdsDropdownSearchInputHeader,
} from "@/design-system/components/dropdown";
import {
  CollectionItem,
  CollectionItemMode,
  CollectionForOrganize,
  type Direction,
} from "@/components/note/editor/top-bar/organize";
import { isNumber } from "lodash-es";
import {
  AutoOrganizeMenuItem,
  SectionDividerMenuItem,
} from "@/components/note/editor/top-bar/organize/OrganizeComboBox/menu-items";

// This is currently built to power the in-editor Organize dropdown
// But it's a good starting point to extract a generic ComboBox component

const AUTO_INDEX = -1;

interface OrganizeComboProps extends Omit<MdsDropdownProps, "contentList"> {
  isFiltering?: boolean;
  searchText: string;
  header: string | null;
  items: CollectionForOrganize[];
  showCreateNewItem: boolean;
  showAutoOrganize?: boolean;
  onInputChange: (value: string) => void;
  onSelectItem: (collectionId: string) => void;
  onCreateNewItem: (name: string) => void;
  onClear: () => void;
  onAutoOrganize?: () => void;
}

export const OrganizeComboBox = ({
  searchText,
  header,
  items,
  showCreateNewItem,
  showAutoOrganize = false,
  onSelectItem,
  onCreateNewItem,
  onInputChange,
  onClear,
  onOpenChange,
  isFiltering,
  onAutoOrganize,
  ...dropdownProps
}: OrganizeComboProps) => {
  const [selectedItem, setSelectedItem] = useState<number>(() => {
    if (showAutoOrganize) return AUTO_INDEX;
    if (items.length > 0) return 0;
    if (showCreateNewItem) return items.length;
    return -1;
  });

  const selectableOrder = useMemo(() => {
    const order: number[] = [];
    if (showAutoOrganize) order.push(AUTO_INDEX);
    for (let i = 0; i < items.length; i += 1) {
      order.push(i);
    }

    if (showCreateNewItem) order.push(items.length);
    return order;
  }, [items.length, showAutoOrganize, showCreateNewItem]);

  // keyboard handling and type-ahead is written custom for this component
  // TODO: consider using Floating UI features for this instead
  // https://floating-ui.com/docs/useListNavigation
  // https://floating-ui.com/docs/FloatingList
  // https://floating-ui.com/docs/useTypeahead
  const handleKeyDirection = useCallback(
    (direction: Direction) => {
      if (selectableOrder.length === 0) return;

      const directionOffset = direction === "up" ? -1 : 1;
      const currentIndex = selectableOrder.indexOf(selectedItem);

      if (currentIndex === -1) {
        const fallbackIndex = direction === "up" ? selectableOrder.length - 1 : 0;
        setSelectedItem(selectableOrder[fallbackIndex]);
        return;
      }

      const nextIndex =
        (currentIndex + directionOffset + selectableOrder.length) % selectableOrder.length;
      setSelectedItem(selectableOrder[nextIndex]);
    },
    [selectableOrder, selectedItem]
  );

  // always start with the first item selected
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      onOpenChange?.(isOpen);

      if (isOpen) {
        const first = selectableOrder[0];

        if (isNumber(first)) {
          setSelectedItem(first);
        }
      }
    },
    [onOpenChange, selectableOrder]
  );

  const handleEscape = useCallback(() => {
    if (!isFiltering) {
      handleOpenChange(false);
    } else {
      onInputChange("");
    }
  }, [handleOpenChange, isFiltering, onInputChange]);

  const handleEnter = useCallback(() => {
    const createIndex = items.length;

    if (selectedItem === AUTO_INDEX && showAutoOrganize) {
      onAutoOrganize?.();
    } else if (selectedItem === createIndex && showCreateNewItem) {
      onCreateNewItem(searchText);
    } else if (selectedItem >= 0) {
      onSelectItem(items[selectedItem].id);
    }

    const first = selectableOrder[0];
    if (isNumber(first)) {
      setSelectedItem(first);
    }
  }, [
    items,
    onAutoOrganize,
    onCreateNewItem,
    onSelectItem,
    searchText,
    selectableOrder,
    selectedItem,
    showAutoOrganize,
    showCreateNewItem,
  ]);

  const mdsDropdownItems: MdsDropdownItem[] = useMemo(() => {
    const ddItems: MdsDropdownItem[] = [];

    if (showAutoOrganize) {
      ddItems.push({
        kind: MdsDropdownItemKind.Other,
        id: "auto-organize",
        content: (
          <AutoOrganizeMenuItem
            isSelected={selectedItem === AUTO_INDEX}
            onMouseEnter={() => setSelectedItem(AUTO_INDEX)}
            onMouseLeave={() => setSelectedItem(-1)}
            onClick={() => {
              setSelectedItem(AUTO_INDEX);
              onAutoOrganize?.();
            }}
          />
        ),
      });

      if (items.length > 0 || showCreateNewItem) {
        ddItems.push({
          kind: MdsDropdownItemKind.Other,
          id: "auto-organize-divider",
          content: <SectionDividerMenuItem aria-hidden="true" />,
        });
      }
    }

    if (header) {
      ddItems.push({
        kind: MdsDropdownItemKind.Detail,
        id: "header",
        text: header,
      });
    }

    items.forEach((item, i) => {
      ddItems.push({
        kind: MdsDropdownItemKind.Other,
        id: item.id,
        content: (
          <CollectionItem
            title={item.title}
            noteCount={item.noteCount}
            collectionId={item.id}
            subtitle={item.description}
            mode={CollectionItemMode.SELECT}
            isShared={item.isShared}
            isCollected={item.isCollected}
            onClick={() => {
              onSelectItem(item.id);
            }}
            isSelected={i === selectedItem}
            onMouseEnter={() => {
              setSelectedItem(i);
            }}
            onMouseLeave={() => {
              setSelectedItem(-1);
            }}
          />
        ),
      });
    });

    return ddItems;
  }, [
    header,
    items,
    onAutoOrganize,
    onSelectItem,
    selectedItem,
    showAutoOrganize,
    showCreateNewItem,
  ]);

  // Add an item for each Collection

  const contentList = {
    items: mdsDropdownItems,
    header: {
      children: (
        <MdsDropdownSearchInputHeader
          onChange={onInputChange}
          onClear={onClear}
          value={searchText}
          onKeyDirection={handleKeyDirection}
          onEscape={handleEscape}
          onEnter={handleEnter}
          placeholder="Add to existing or new collection"
        />
      ),
      height: 50,
    },
    footer: showCreateNewItem
      ? {
          children: (
            <CollectionItem
              title={searchText.trim()}
              noteCount={0}
              collectionId="new-collection"
              subtitle="Create new collection"
              mode={CollectionItemMode.CREATE}
              isSelected={selectedItem === items.length}
              isCollected={false}
              onMouseEnter={() => {
                setSelectedItem(items.length);
              }}
              onMouseLeave={() => {
                setSelectedItem(-1);
              }}
              onClick={() => {
                onCreateNewItem(searchText);
              }}
            />
          ),
          height: 50,
        }
      : undefined,
  };

  return (
    <MdsDropdown
      contentList={contentList}
      onOpenChange={handleOpenChange}
      {...dropdownProps}
      placement="below-right-alignment"
      innerStyles={{
        Row: {
          className: rowStyles,
        },
        Content: {
          className: contentStyles,
        },
      }}
    />
  );
};

const rowStyles = css({
  overflow: "visible",
});

const contentStyles = css({
  maxHeight: 320,
  minWidth: 320,
  maxWidth: 320,
});
